import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SharingContext } from '../../../models/sharing-context';
import { ApplicationStateService } from '../../../services/application-state.service';
import { ClipboardService } from 'ngx-clipboard';
import { PageService } from '../../../services/page.service';
import { ModalService } from '../../../services/modal.service';
import {
    ClassSharingModalComponent
} from '../../bookviewer/pages/modal-container/class-sharing-modal/class-sharing-modal.component';
import { SchoolClassService } from '../../../services/school-class.service';
import { RealPageNumberPipe } from '../../../pipes/real-page-number.pipe';
import { AssetPipe } from '../../../pipes/asset.pipe';

@Component({
    selector: 'app-sharing-options',
    templateUrl: './sharing-options.component.html',
    styleUrls: ['./sharing-options.component.scss'],
    standalone: true,
    imports: [
        RealPageNumberPipe,
        AssetPipe
    ]
})
export class SharingOptionsComponent implements OnInit {

    @Input() sharingContext: SharingContext;
    @Input() homeworkModalId!: string;
    @Output() readonly platformLinkCopied = new EventEmitter<{
        platformName: string;
        logoURL: string;
        heading: string;
        description: string;
    }>();
    emailLink: string;
    msTeamsLink: string;

    constructor(public applicationStateService: ApplicationStateService,
                private pageService: PageService,
                private clipboardService: ClipboardService,
                public schoolClassService: SchoolClassService,
                private modalService: ModalService) {
    }

    ngOnInit(): void {
        this.generateLinks();
    }

    openURL(url): void {
        window.open(url, '_blank').focus();
    }

    copyLinkToClipboard(platform?: string): void {
        const link = this.getContextLink();
        if (this.sharingContext.homework) {
            this.clipboardService.copyFromContent(link, document.getElementById('homework-Modal'));
            this.clipboardService.destroy(document.getElementById('homework-Modal'));
        }
        if (this.sharingContext.whiteboard) {
            this.clipboardService.copyFromContent(link, document.getElementById('whiteboardVideoModal'));
        } else {
            const htmlElement = document.querySelector<HTMLElement>('body.bookviewer');
            this.clipboardService.copyFromContent(link, htmlElement);
            if (htmlElement) {
                this.clipboardService.destroy(htmlElement);
            }
        }
        if (platform) {
            this.platformLinkCopied.emit(this.getPlatform(platform));
        } else {
            this.showCopyHud();
        }

    }

    classSharingModal(): void {
        this.schoolClassService.setSharingLink(`Link: ${this.getContextLink()}`);
        const homeworkModal = document.getElementById(this.homeworkModalId);

        if (homeworkModal) {
            homeworkModal.classList.add('dimmed');
        }
        this.modalService.openModal(ClassSharingModalComponent);

        const classSharingModalId = 'classSharingModal';
        const classSharingModal = document.getElementById(classSharingModalId);
        if (classSharingModal) {
            classSharingModal.addEventListener('hidden.bs.modal', () => {
                if (homeworkModal) {
                    homeworkModal.classList.remove('dimmed');
                }
            });
        }
    }

    private generateLinks(): void {
        this.generateEmailLink();
        this.generateMSTeamsLink();
    }

    private generateEmailLink(): void {
        const link = this.getContextLink();
        if (this.sharingContext.homework) {
            this.emailLink = this.generateMailToLink(link, this.sharingContext.homework.title, this.sharingContext.homework.email);
        } else if (this.sharingContext.page) {
            const currentPage = this.pageService.getRealPageNumber(this.sharingContext.page.index);
            const bookTitle = this.applicationStateService.bookConfig.name;
            this.emailLink = this.generateMailToLink(link, `Frage zu ${bookTitle} auf Seite ${currentPage}`);
        } else if (this.sharingContext.whiteboard) {
            this.emailLink = this.generateMailToLink(link, `Whiteboard Aufzeichnung ${this.sharingContext.whiteboard.name}`);
        } else {
            this.emailLink = this.generateMailToLink(link);
        }
    }

    private generateMSTeamsLink(): void {
        const link = this.getContextLink();
        let subject = '';

        if (this.sharingContext.homework) {
            subject = this.sharingContext.homework.title;
        } else if (this.sharingContext.page) {
            const currentPage = this.pageService.getRealPageNumber(this.sharingContext.page.index);
            const bookTitle = this.applicationStateService.bookConfig.name;
            subject = `Frage zu ${bookTitle} auf Seite ${currentPage}`;
        } else if (this.sharingContext.whiteboard) {
            subject = `Whiteboard Aufzeichnung ${this.sharingContext.whiteboard.name}`;
        }

        this.msTeamsLink = `https://teams.microsoft.com/l/chat/0/0?users=&topicName=${subject}&message=${link}`;
    }

    private getContextLink(): string {
        if (this.sharingContext.homework) {
            return `https://${this.sharingContext.homework.domain}/${this.sharingContext.homework.link}`;
        } else if (this.sharingContext.page) {
            const currentPage = this.pageService.getRealPageNumber(this.sharingContext.page.index);
            const host = location.host.charAt(location.host.length - 1) === '/' ?
                location.host.substring(0, location.host.length - 1) : location.host;
            const path = location.pathname.charAt(0) === '/' ?
                location.pathname.substring(1, location.pathname.length) : `${location.pathname}`;

            return `${location.protocol}//${host}/${path}?p=${currentPage}`;
        }

        return this.sharingContext.link;
    }

    private generateMailToLink(body: string, subject = '', to = ''): string {
        return `mailto:${to}?to=&body=${body}&subject=${subject}`;
    }

    private showCopyHud(): void {
        const copyElement = $('.copy-hud');
        copyElement.fadeIn('slow', () => {
            setTimeout(() => {
                copyElement.fadeOut('slow');
            }, 2000);
        });
    }

    private getPlatform(platform: string): {
        platformName: string;
        logoURL: string;
        heading: string;
        description: string;
    } {

        switch (platform) {

            case 'eduvidual': {
                return {
                    platformName: 'Eduvidual',
                    logoURL: 'sharing/share-eduvidual.png',
                    heading: 'Moodle / Eduvidual',
                    description: 'Du kannst den Link jetzt an der gewünschten Stelle im Moodle Chat oder in einem Moodle Kurs einfügen.'
                };
            }
            case 'msteams': {
                return {
                    platformName: 'Microsoft Teams',
                    logoURL: 'sharing/share-microsoft.png',
                    heading: 'Microsoft Teams',
                    description: 'Du kannst den Link jetzt in einem Chat in Microsoft Teams einfügen.'
                };
            }
            default:
                return undefined;

        }
    }
}
